// OpenClaw skill-workshop bridge. Agents on the gateway propose skills
// with the native `skill_workshop` tool; those proposals live in the
// container, NOT in Tier 2, so the Skills → Pending queue can't see
// them on its own. These hooks read them and promote the good ones
// into `learned_skills` (status pending_review) for the normal
// approve flow.

import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import type { LearnedSkill } from "./skills";

export interface WorkshopProposal {
  id: string;
  agent: string | null;
  skillName: string;
  title: string;
  kind: "create" | "update" | "revise" | string;
  status: "pending" | "applied" | "rejected" | "quarantined" | string;
  scanState: string | null;
  createdAt: number; // epoch seconds
  createdAtIso: string | null;
  updatedAtIso: string | null;
  description: string | null;
  body: string | null; // only on inspect
  promotedSkillId: number | null;
}

export interface WorkshopAgentStatus {
  agent: string;
  count: number;
  error: { error: string; status: number | null; message: string } | null;
}

export interface WorkshopProposalsResponse {
  proposals: WorkshopProposal[];
  agents: WorkshopAgentStatus[];
}

const key = (customerId: string | null) => ["skill-workshop", customerId];

export function useWorkshopProposals(customerId: string | null, poll?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: key(customerId),
    enabled: customerId !== null,
    queryFn: ({ signal }) =>
      apiFetch<WorkshopProposalsResponse>(
        `/api/customers/${customerId}/skill-workshop/pending`,
        { signal },
      ),
    // Same cadence as the Tier 2 pending queue — a fresh "save this as
    // a skill" in chat should surface within a refetch.
    staleTime: 10_000,
    refetchInterval: poll?.refetchInterval ?? 15_000,
  });
}

/**
 * One proposal with its body (the list leaves the body out). Shared by the
 * detail view, the review queue's prefetch and the check before a bulk
 * approve, so all three read one cache entry per proposal.
 */
export function workshopProposalQuery(customerId: string | null, agent: string | null, proposalId: string | null) {
  return queryOptions({
    queryKey: [...key(customerId), agent, proposalId],
    queryFn: ({ signal }) =>
      apiFetch<WorkshopProposal>(
        `/api/customers/${customerId}/agents/${agent}/skill-workshop/${encodeURIComponent(proposalId ?? "")}`,
        { signal },
      ),
    staleTime: 60_000,
  });
}

export function useWorkshopProposal(
  customerId: string | null,
  agent: string | null,
  proposalId: string | null,
  enabled = true,
) {
  return useQuery({
    ...workshopProposalQuery(customerId, agent, proposalId),
    enabled: enabled && !!customerId && !!agent && !!proposalId,
  });
}

interface ProposalArgs {
  customerId: string;
  agent: string;
  proposalId: string;
}

function invalidate(qc: ReturnType<typeof useQueryClient>, customerId: string) {
  void qc.invalidateQueries({ queryKey: key(customerId) });
  void qc.invalidateQueries({ queryKey: ["skills", "pending", customerId] });
}

// Promote returns the learned_skills row it created (status
// pending_review, scope customer): approve that id to finish the review.
export function usePromoteProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, agent, proposalId }: ProposalArgs) =>
      apiFetch<LearnedSkill>(
        `/api/customers/${customerId}/agents/${agent}/skill-workshop/${encodeURIComponent(proposalId)}/promote`,
        { method: "POST" },
      ),
    onSuccess: (_, vars) => invalidate(qc, vars.customerId),
  });
}

export function useRejectProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      customerId,
      agent,
      proposalId,
      reason,
    }: ProposalArgs & { reason: string }) =>
      apiFetch<unknown>(
        `/api/customers/${customerId}/agents/${agent}/skill-workshop/${encodeURIComponent(proposalId)}/reject`,
        { method: "POST", body: { reason } },
      ),
    onSuccess: (_, vars) => invalidate(qc, vars.customerId),
  });
}

// Plain calls for loops (bulk review). They skip the hooks' cache work so a
// run of N rows does not start N gateway re-listings: the caller
// invalidates once with invalidateReviewQueues when the loop ends.

export function promoteProposal({ customerId, agent, proposalId }: ProposalArgs): Promise<LearnedSkill> {
  return apiFetch<LearnedSkill>(
    `/api/customers/${customerId}/agents/${agent}/skill-workshop/${encodeURIComponent(proposalId)}/promote`,
    { method: "POST" },
  );
}

export function rejectProposal({ customerId, agent, proposalId, reason }: ProposalArgs & { reason: string }): Promise<unknown> {
  return apiFetch<unknown>(
    `/api/customers/${customerId}/agents/${agent}/skill-workshop/${encodeURIComponent(proposalId)}/reject`,
    { method: "POST", body: { reason } },
  );
}

/** Refresh everything a review decision changes: proposals, the Tier 2 queue, installed skills. */
export function invalidateReviewQueues(qc: ReturnType<typeof useQueryClient>, customerId: string) {
  invalidate(qc, customerId);
  void qc.invalidateQueries({ queryKey: ["skills", "active", customerId] });
}
